import { Film, ImageIcon } from 'lucide-react';

import { BeforeAfterGallery } from '@/components/ai-tools/before-after-gallery';
import { SwatchCardGrid } from '@/components/ai-tools/content-sections';
import { SampleGallery } from '@/components/ai-tools/sample-gallery';
import { localized, type SinglePhotoTool } from '@/config/ai-tools';
import type { SlotEntry } from '@/lib/store/recommendation-slot';
import type { ToolSummary } from '@/modules/directory/types';
import { m } from '@/paraglide/messages.js';
import { getLocale } from '@/paraglide/runtime.js';
import { PhotoStyleStudio } from './photo-style-studio';
import { resolvePhotoToolCopy } from './photo-tool-copy';
import { ToolPageFrame } from './tool-page-frame';

/**
 * Trend page for a one-photo generator (photo-to-photo image or photo-video
 * clip). The shared page rhythm lives in `ToolPageFrame`; this supplies the
 * studio, the example gallery and the image/video wording. Video trends lead
 * with clips everywhere; stills are only their posters.
 */
export function PhotoStyleToolPage({
  tool,
  slot,
  recentRenders,
}: {
  tool: SinglePhotoTool;
  slot: SlotEntry<ToolSummary>[];
  recentRenders: number | null;
}) {
  const locale = getLocale();
  const t = (value: Parameters<typeof localized>[0]) => localized(value, locale);
  const copy = resolvePhotoToolCopy(tool, locale);
  const video = tool.kind === 'photo-video';
  const styleLabel = (id: string | undefined) => {
    const style = tool.styles.find((item) => item.id === id);
    return style ? t(style.label) : undefined;
  };

  return (
    <ToolPageFrame
      tool={tool}
      slot={slot}
      recentRenders={recentRenders}
      heroExamples={
        tool.kind === 'photo-video' ? tool.examples.map((example) => ({ ...example, label: t(example.label) })) : []
      }
      studio={(onNeedCredits) => <PhotoStyleStudio tool={tool} recommendations={slot} onNeedCredits={onNeedCredits} />}
      trustItem={{ icon: video ? Film : ImageIcon, title: copy.trustTitle, body: copy.trustBody }}
      gallery={
        tool.examples.length > 0
          ? {
              title: copy.galleryTitle,
              description: copy.galleryDescription,
              content:
                tool.kind === 'photo-video' ? (
                  <SampleGallery
                    items={tool.examples.map((example) => ({
                      video: example.video,
                      poster: example.poster,
                      title: t(example.label),
                      description: example.description ? t(example.description) : undefined,
                      tag: styleLabel(example.stage),
                    }))}
                  />
                ) : (
                  <BeforeAfterGallery
                    subject={t(tool.name)}
                    labels={{ before: m['directory.ai_tools.photo.before'](), after: m['directory.ai_tools.photo.after']() }}
                    items={tool.examples.map((example) => ({
                      before: example.before,
                      after: example.after,
                      title: t(example.label),
                      tag: styleLabel(example.style),
                    }))}
                  />
                ),
            }
          : null
      }
      options={{
        eyebrow: m['directory.ai_tools.photo.styles_eyebrow'](),
        title: copy.stylesTitle,
        content: (
          <SwatchCardGrid
            items={tool.styles.map((style) => ({
              id: style.id,
              title: t(style.label),
              body: t(style.description),
              swatch: style.swatch,
              image: style.image,
              video: style.video,
            }))}
          />
        ),
      }}
      ideasTitle={copy.ideasTitle}
      relatedTitle={m['directory.ai_tools.related_title']()}
      costs={{
        caption: video ? m['directory.ai_tools.costs.title']() : m['directory.ai_tools.photo.costs_title'](),
        labelHeader: m['directory.ai_tools.costs.quality'](),
        valueHeader: video ? m['directory.ai_tools.costs.credits']() : m['directory.ai_tools.photo.costs_credits'](),
      }}
      packsUnit={video ? 'video' : 'image'}
      standardCredits={Math.min(...tool.qualities.map((quality) => quality.credits))}
      cta={{
        title: video ? m['directory.ai_tools.cta_title']() : m['directory.ai_tools.photo.cta_title'](),
        description: copy.ctaDescription,
        button: copy.ctaButton,
      }}
    />
  );
}
